import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDate, fmtMoney, fmtLongToday } from "@/lib/format";
import { StatusPill, ProgressBar, sponsorPill } from "@/components/Badges";
import SponsorForm from "@/components/SponsorForm";
import ContactSponsorsDialog from "@/components/ContactSponsorsDialog";

const Kpi = ({ label, value, children, testId, delay }) => (
  <div className="card p-5 fade-up" style={{ animationDelay: `${delay}ms` }} data-testid={testId}>
    <p className="text-sm text-[#5B665F]">{label}</p>
    <p className="num text-3xl sm:text-4xl font-bold mt-2 tracking-tight" data-testid={`${testId}-value`}>{value}</p>
    <div className="mt-3 text-[13px]">{children}</div>
  </div>
);

export const CpCell = ({ s }) => (
  <div className="flex items-center gap-3 min-w-[150px]">
    <div className="w-28 shrink-0"><ProgressBar value={s.cp_delivered} total={s.cp_total} className="h-1.5" /></div>
    <span className="text-sm whitespace-nowrap">{s.cp_delivered} / {s.cp_total}</span>
  </div>
);

function TodoBlock({ todo }) {
  return (
    <section className="card mt-6 fade-up" data-testid="todo-block">
      <h2 className="text-lg font-bold px-5 sm:px-6 py-5 border-b border-[#E1E4DD]">À faire cette semaine</h2>
      {todo.length === 0 && <p className="px-6 py-6 text-sm text-[#5B665F]" data-testid="todo-empty">Rien d'urgent : tout est à jour.</p>}
      {todo.map((t, i) => (
        <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 sm:px-6 py-4 border-b last:border-b-0 border-[#E1E4DD]" data-testid={`todo-item-${i}`}>
          <StatusPill status={t.type}>{t.type === "en_retard" ? "En retard" : "À renouveler"}</StatusPill>
          <p className="text-sm flex-1">
            <span className="font-semibold">{t.sponsor_name}</span>
            <span className="text-[#5B665F]"> · {t.type === "en_retard" ? `${t.text} (prévu le ${fmtDate(t.date)})` : `${t.text} le ${fmtDate(t.date)}, bilan à envoyer avant le rendez-vous`}</span>
          </p>
          {t.type === "en_retard"
            ? <Link to={`/sponsors/${t.sponsor_id}`} className="btn-outline btn-sm self-start sm:self-auto" data-testid={`todo-view-${i}`}>Voir la fiche</Link>
            : <Link to={`/bilans/${t.sponsor_id}`} className="btn-primary btn-sm self-start sm:self-auto" data-testid={`todo-bilan-${i}`}>Générer le bilan</Link>}
        </div>
      ))}
    </section>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const welcome = params.get("bienvenue") === "1";
  const load = () => api.get("/dashboard").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <p className="text-[#5B665F]" data-testid="dashboard-loading">Chargement…</p>;
  const k = d.kpis;
  return (
    <div data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-sm text-[#5B665F]">{fmtLongToday()}</p>
          <h1 className="text-3xl sm:text-4xl lg:text-[40px] font-bold tracking-tight mt-1">Vos partenaires, en un coup d'œil</h1>
        </div>
        <div className="flex flex-wrap gap-2 self-start sm:self-auto">
          <button className="btn-outline" onClick={() => setContactOpen(true)} data-testid="dashboard-contact-sponsors-button">Contacter tous mes sponsors</button>
          <button className="btn-primary" onClick={() => setFormOpen(true)} data-testid="dashboard-add-sponsor-button">Ajouter un sponsor</button>
        </div>
      </div>
      {welcome && (
        <div className="card mt-6 p-5 border-[#1E6B45] bg-[#DFF3E6] flex flex-col sm:flex-row sm:items-center gap-3" data-testid="welcome-banner">
          <p className="flex-1 text-[15px] text-[#145A36]"><b>Bienvenue sur Partenia !</b> Votre club est créé. Ajoutez votre premier sponsor pour commencer.</p>
          <button className="btn-outline btn-sm" onClick={() => setParams({})} data-testid="welcome-close">Fermer</button>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Kpi label="Sponsors actifs" value={k.active} testId="kpi-active" delay={0}><span className="text-[#5B665F]">sur {d.sponsors.length} au total</span></Kpi>
        <Kpi label="Montant de la saison" value={fmtMoney(k.amount)} testId="kpi-amount" delay={60}><span className="text-[#5B665F]" data-testid="kpi-paid">dont {fmtMoney(k.paid)} encaissés</span></Kpi>
        <Kpi label="Contreparties livrées" value={`${k.delivered} / ${k.total}`} testId="kpi-delivered" delay={120}><ProgressBar value={k.delivered} total={k.total} testId="kpi-delivered-bar" /></Kpi>
        <Kpi label="Renouvellements sous 60 jours" value={k.renewals} testId="kpi-renewals" delay={180}><span className="text-[#1F4396]">Bilans à envoyer avant le rendez-vous</span></Kpi>
      </div>
      <TodoBlock todo={d.todo} />
      <section className="card mt-6 overflow-hidden fade-up" data-testid="sponsors-table-block">
        <div className="flex items-center justify-between px-5 sm:px-6 py-5">
          <h2 className="text-lg font-bold">Vos sponsors</h2>
          <Link to="/sponsors" className="text-sm link" data-testid="dashboard-see-all">Tout voir</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-[#F8F9F6] text-[#5B665F] text-[13px]">
              <tr>{["Sponsor", "Niveau", "Montant", "Contreparties", "Fin de contrat", "Statut"].map((h) => <th key={h} className="text-left font-medium px-6 py-3">{h}</th>)}</tr>
            </thead>
            <tbody>
              {d.sponsors.map((s) => (
                <tr key={s.id} className="border-t border-[#E1E4DD] hover:bg-[#FAFBF8] transition-colors" data-testid={`dashboard-sponsor-row-${s.id}`}>
                  <td className="px-6 py-4"><Link to={`/sponsors/${s.id}`} className="link font-medium" data-testid={`dashboard-sponsor-link-${s.id}`}>{s.name}</Link></td>
                  <td className="px-6 py-4">{s.level}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{fmtMoney(s.amount)}</td>
                  <td className="px-6 py-4"><CpCell s={s} /></td>
                  <td className="px-6 py-4 whitespace-nowrap">{fmtDate(s.end_date)}</td>
                  <td className="px-6 py-4"><StatusPill status={s.status} testId={`dashboard-status-${s.id}`}>{sponsorPill(s)}</StatusPill></td>
                </tr>
              ))}
              {d.sponsors.length === 0 && <tr><td colSpan={6} className="px-6 py-8 text-[#5B665F]">Aucun sponsor pour l'instant.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
      <SponsorForm open={formOpen} onOpenChange={setFormOpen} onSaved={load} />
      <ContactSponsorsDialog open={contactOpen} onOpenChange={setContactOpen} />
    </div>
  );
}
